import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import { useActions } from '../../../../../hooks/useActions';
import { useNavigateSearch } from '../../../../../hooks/useNavigateSearch';
import { useProcessing } from '../../../../../hooks/useProcessing';
import {
  EditProductSubscriptionPageParams,
  EditProductSubscriptionRenewPageParams,
  ExtendProductSubscriptionPageParams,
  RoutePath,
} from '../../../../../models/enums/RoutePath';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { SUBSCRIPTION_OVERVIEW_LOADING_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { organizationNameSelector } from '../../../../../store/redux-slices/organizations/selectors';
import { ProductType } from '../../../../../models/ProductType';
import { DELETE_PRODUCT_SUBSCRIPTION_MODAL_NAME } from '../../../../../styledComponents/Modals/DeleteSubscriptionModal/deleteSubscriptionModal.const';
import { DeleteSubscriptionModalDetails } from '../../../../../styledComponents/Modals/DeleteSubscriptionModal/deleteSubscriptionModal.model';
import { ProductSubscriptionOverviewPageParamsType } from '../../../../../models/types/RoutePath';
import { loadTLSubscriptionOverview } from '../../../../../store/saga-slices/subscriptions/travelLicense';
import { clearTLSubscriptionOverview } from '../../../../../store/redux-slices/subscriptions/travelLicense';
import { tlSubscriptionOverviewSelector } from '../../../../../store/redux-slices/subscriptions/travelLicense/selectors';
import { EDIT_TL_SUBSCRIPTION_OPTIONS_MODAL_NAME } from './Modals/TLSubscriptionEditOptionsModal/tlSubscriptionEditOptionsModal.const';
import { textContainsWords } from '../../../../../utils/textHelpers';
import { RequiredQueryParams } from './tlSubscriptionOverview.models';
import { ILoadTLSubscriptionOverviewPayload } from '../../../../../store/saga-slices/subscriptions/travelLicense/models';
import { EditRenewalSubscriptionStatusModalDetails } from '../../../../../styledComponents/Modals/EditRenewalSubscriptionStatusModal/editRenewalSubscriptionStatusModal.models';
import { EDIT_RENEWAL_SUBSCRIPTION_STATUS_MODAL_NAME } from '../../../../../styledComponents/Modals/EditRenewalSubscriptionStatusModal/editRenewalSubscriptionStatusModal.const';

export const useTLSubscriptionOverviewTabPage = () => {
  const { id, subscriptionId } = useParams();
  const navigate = useNavigateSearch();

  const LoadSubscriptionOverview = useActions(loadTLSubscriptionOverview);
  const ClearCurrentSubscription = useActions(clearTLSubscriptionOverview);

  const subscriptionOverviewLoading = useProcessing(
    SUBSCRIPTION_OVERVIEW_LOADING_PROCESSING,
  );
  const subscriptionOverview = useSelector(tlSubscriptionOverviewSelector);

  const location = useLocation();

  const loadSubscriptionOverviewHandler = useCallback(() => {
    const params = location.search.slice(1);

    if (params && !textContainsWords(params, RequiredQueryParams)) {
      return;
    }

    const payload: ILoadTLSubscriptionOverviewPayload = {
      organizationId: id,
      subscriptionId,
      params,
    };

    LoadSubscriptionOverview(payload);
  }, [LoadSubscriptionOverview, id, location.search, subscriptionId]);

  useEffect(() => {
    loadSubscriptionOverviewHandler();
  }, [loadSubscriptionOverviewHandler]);

  useEffect(
    () => () => {
      ClearCurrentSubscription();
    },
    [ClearCurrentSubscription],
  );

  return {
    id,
    subscriptionId,
    navigate,
    subscriptionOverview,
    subscriptionOverviewLoading,
    loadSubscriptionOverviewHandler,
  };
};

export const useTLSubscriptionOverviewActions = () => {
  const { t } = useTranslation();
  const { id, subscriptionId, subscriptionType } =
    useParams<ProductSubscriptionOverviewPageParamsType>();
  const organizationName = useSelector(organizationNameSelector);
  const UpdateModalState = useActions(updateModalState);
  const navigate = useNavigateSearch();
  const subscriptionOverview = useSelector(tlSubscriptionOverviewSelector);

  const deleteProductSubscriptionHandler = () => {
    const details: DeleteSubscriptionModalDetails = {
      name: t('phrase.travel-license-for', {
        value: organizationName,
      }),
      organizationId: id,
      subscriptionId,
      productType: ProductType.TravelLicense,
    };

    UpdateModalState([DELETE_PRODUCT_SUBSCRIPTION_MODAL_NAME, details]);
  };

  const editSubscriptionHandler = () => {
    if (subscriptionOverview?.isRenewation) {
      navigate(RoutePath.EditClientSubscriptionRenewPage, {
        params: {
          [EditProductSubscriptionRenewPageParams.OrganizationId]: id,
          [EditProductSubscriptionRenewPageParams.SubscriptionId]:
            subscriptionId,
          [EditProductSubscriptionRenewPageParams.ProductType]:
            ProductType.TravelLicense,
        },
      });
      return;
    }

    navigate(RoutePath.EditProductSubscription, {
      params: {
        [EditProductSubscriptionPageParams.OrganizationId]: id,
        [EditProductSubscriptionPageParams.SubscriptionId]: subscriptionId,
        [EditProductSubscriptionPageParams.SubscriptionType]:
          ProductType.TravelLicense,
      },
    });
  };

  const extendProductSubscriptionHandler = () => {
    navigate(RoutePath.ExtendProductSubscription, {
      params: {
        [ExtendProductSubscriptionPageParams.OrganizationId]: id,
        [ExtendProductSubscriptionPageParams.SubscriptionId]: subscriptionId,
        [ExtendProductSubscriptionPageParams.ProductType]:
          ProductType.TravelLicense,
      },
    });
  };

  const showEditSubscriptionDetailsModal = () => {
    UpdateModalState([
      EDIT_TL_SUBSCRIPTION_OPTIONS_MODAL_NAME,
      { data: subscriptionOverview },
    ]);
  };

  const editRenewalStatusHandler = () => {
    const details: EditRenewalSubscriptionStatusModalDetails = {
      organizationId: id,
      subscriptionId,
      renewalStatus: subscriptionOverview?.renewalStatus,
      contractName: subscriptionOverview?.organizationContractName,
      organizationName,
      expirationDate: subscriptionOverview?.effectiveDateTo,
      productType: subscriptionType,
    };

    UpdateModalState([EDIT_RENEWAL_SUBSCRIPTION_STATUS_MODAL_NAME, details]);
  };

  return {
    extendProductSubscriptionHandler,
    editSubscriptionHandler,
    deleteProductSubscriptionHandler,
    showEditSubscriptionDetailsModal,
    editRenewalStatusHandler,
  };
};
